import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import {
  User,
  LayoutDashboard,
  CalendarClock,
  Users,
  UserPlus,
  Settings,
  FlaskConical,
  BarChart3,
  type LucideIcon,
} from 'lucide-react';
import { PatientRecord } from '@/assets/icons';
import { useAuthStore } from '@/lib/stores/authStore';
import { MenuItem, ROLE_CONFIG, UserRole } from '@/lib/types/authorization';
import { useScale } from '@/lib/hooks/useScale';

/**
 * Route-based icon configuration
 * Maps route paths to their icons and styles - THIS IS FRONTEND ONLY
 * Backend only sends { title, link } - icons are determined here
 */
const ROUTE_ICON_CONFIG: Record<
  string,
  { icon: LucideIcon | React.FC<{ className?: string }>; filled?: boolean }
> = {
  '/dashboard': { icon: LayoutDashboard, filled: true },
  '/hospital-dashboard': { icon: BarChart3, filled: false },
  '/practitioners': { icon: Users, filled: false },
  '/onboarding': { icon: UserPlus, filled: false },
  '/appointments': { icon: CalendarClock, filled: false },
  '/records': { icon: PatientRecord, filled: true },
  '/investigation-orders': { icon: FlaskConical, filled: false },
  '/practitioner': { icon: User, filled: false },
  '/profile': { icon: User, filled: false },
  '/my-profile': { icon: User, filled: false },
  '/my-appointments': { icon: CalendarClock, filled: false },
  '/my-records': { icon: PatientRecord, filled: true },
  '/settings': { icon: Settings, filled: false },
};

/**
 * Get icon config for a route
 */
function getRouteIconConfig(link: string): {
  icon: LucideIcon | React.FC<{ className?: string }>;
  filled: boolean;
} {
  const config = ROUTE_ICON_CONFIG[link];
  if (config) {
    return { icon: config.icon, filled: config.filled ?? false };
  }
  // Default fallback
  return { icon: LayoutDashboard, filled: false };
}

interface DynamicNavProps {
  /** Custom class name */
  className?: string;
}

/**
 * DynamicNav Component
 *
 * Renders navigation based on user's UserConfiguration menu.
 * Falls back to role-based default menu if no config exists.
 *
 * @security
 * - Menu visibility is UI only - NOT security
 * - Backend authorization enforces actual data access
 * - Routes still protected by ProtectedRoute component
 */
export function DynamicNav({ className }: DynamicNavProps) {
  const location = useLocation();
  const { getMenu } = useAuthStore();
  const [hoveredNav, setHoveredNav] = useState<string | null>(null);

  const menuItems = getMenu();

  const isActivePath = (path: string) => {
    // Exact match or starts with path (for nested routes)
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Map menu items with special handling for certain paths
  const getActiveState = (link: string) => {
    if (link === '/records') {
      return (
        isActivePath('/records') || isActivePath('/patient-record') || isActivePath('/patient-form')
      );
    }
    if (link === '/practitioner') {
      return isActivePath('/practitioner');
    }
    if (link === '/practitioners') {
      return isActivePath('/practitioners') || isActivePath('/onboarding');
    }
    return isActivePath(link);
  };

  return (
    <nav className={`flex items-center gap-3 ${className || ''}`}>
      {menuItems.map((item: MenuItem) => {
        const { icon: IconComponent, filled: isFilled } = getRouteIconConfig(item.link);
        const isActive = getActiveState(item.link);
        const navKey = item.link.replace(/\//g, '-');

        return (
          <Link
            key={item.link}
            to={item.link}
            className={`relative flex h-10 items-center gap-2 rounded-xl px-2.5 py-1.5 transition-all ${
              isActive ? 'bg-primary-100 hover:bg-primary-200' : 'bg-gray-50 hover:bg-gray-100'
            }`}
            onMouseEnter={() => setHoveredNav(navKey)}
            onMouseLeave={() => setHoveredNav(null)}
          >
            {isActive && (
              <span className="font-hanken text-[calc(16px*var(--app-scale))] font-medium leading-[calc(20px*var(--app-scale))] text-pranik-brand">
                {item.title}
              </span>
            )}
            <IconComponent
              className={`h-[calc(24px*var(--app-scale))] w-[calc(24px*var(--app-scale))] ${
                isActive
                  ? isFilled
                    ? 'fill-pranik-brand text-pranik-brand'
                    : 'text-pranik-brand'
                  : isFilled
                    ? 'fill-primary text-primary'
                    : 'text-primary'
              }`}
            />
            {hoveredNav === navKey && !isActive && (
              <span className="absolute -bottom-8 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-2 py-1 text-[calc(11px*var(--app-scale))] text-white">
                {item.title}
              </span>
            )}
          </Link>
        );
      })}
    </nav>
  );
}

/**
 * UserRoleBadge Component
 *
 * Displays the current user's role as a badge
 * Uses the role display label from the backend when available.
 */
export function UserRoleBadge() {
  const user = useAuthStore((state) => state.user);
  const scale = useScale();

  if (!user?.role) return null;

  // roleDisplay comes from the backend.
  // Falls back to ROLE_CONFIG label (e.g. 'Administrator'), then raw code.
  const label = user.roleDisplay || ROLE_CONFIG[user.role as UserRole]?.label || user.role;

  const roleColors: Record<string, string> = {
    admin: 'bg-purple-100 text-purple-800',
    frontdesk: 'bg-green-100 text-green-800',
    practitioner: 'bg-blue-100 text-blue-800',
  };

  const colorClass = roleColors[user.role] || 'bg-gray-100 text-gray-800';

  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 font-medium ${colorClass}`}
      style={{ fontSize: `calc(12px * ${scale})` }}
      title={`Role: ${label}`}
    >
      {label}
    </span>
  );
}
